import { useMemo } from "react";
import { Navigate, Outlet, useMatch } from "react-router-dom";
import { Activity, ChartLine } from "lucide-react";
import { SectionLayout } from "@/components/layout/SectionLayout";
import type { SidebarItem } from "@/types/navigation";
import { useDashboardsQuery } from "@/lib/queries";

const SOURCE_ICON: Record<string, React.ElementType | undefined> = {
  events: Activity,
};

export default function MonitoringPage() {
  const matchView = useMatch("/monitoring/:slug");
  const matchInfringement = useMatch("/monitoring/infringements/:id");
  const { data: dashboards = [], isLoading } = useDashboardsQuery();

  const isIndex = !matchView && !matchInfringement;

  const pageTitle = useMemo(() => {
    if (matchView?.params.slug) {
      return dashboards.find((d) => d.slug === matchView.params.slug)?.name ?? "Dashboard";
    }
    if (matchInfringement) return "Infringement";
    return dashboards[0]?.name ?? "Dashboard";
  }, [matchView, matchInfringement, dashboards]);

  const sidebarItems = useMemo<SidebarItem[]>(
    () =>
      dashboards.map((d) => ({
        id: d.id,
        label: d.name,
        href: `/monitoring/${d.slug}`,
        // Use icon of the first source (if any)
        icon: SOURCE_ICON[d.sources?.[0]],
      })),
    [dashboards],
  );

  // Redirect index to first dashboard once data is available
  if (isIndex && dashboards[0]?.slug) {
    return <Navigate to={dashboards[0].slug} replace />;
  }

  return (
    <SectionLayout
      sidebarKey="monitoring"
      sectionTitle="Monitoring"
      sectionIcon={Activity}
      sidebarItems={sidebarItems}
      pageTitle={pageTitle}
      breadcrumbs={[{ title: "Monitoring", href: "/monitoring" }]}
    >
      {isIndex ? (
        <div className="flex flex-col items-center justify-center h-64 gap-4 text-center">
          <ChartLine className="h-10 w-10 text-muted/30" />
          {isLoading ? (
            <p className="text-sm font-medium text-secondary">Loading…</p>
          ) : (
            <p className="text-sm font-medium text-secondary">No dashboard configured</p>
          )}
        </div>
      ) : (
        <Outlet />
      )}
    </SectionLayout>
  );
}
